import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CourseWithUnits, Lesson, ExternalLink, AssetMaterial, CourseFlashcardSummary, ExamSummary, Unit } from '../../types';
import { api } from '../../services/api';
import { formatTimeAgo } from '../../utils/time';
import MarkdownContent from '../shared/MarkdownContent';

// Natural sort key for tiebreaker
function naturalSortKey(text: string): (string | number)[] {
  return text.split(/(\d+)/).map(part => {
    const num = parseInt(part, 10);
    return isNaN(num) ? part.toLowerCase() : num;
  });
}

function compareNatural(a: string, b: string): number {
  const aKey = naturalSortKey(a);
  const bKey = naturalSortKey(b);
  for (let i = 0; i < Math.max(aKey.length, bKey.length); i++) {
    const aVal = aKey[i] ?? '';
    const bVal = bKey[i] ?? '';
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      if (aVal !== bVal) return aVal - bVal;
    } else {
      const cmp = String(aVal).localeCompare(String(bVal));
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

type UnitItem = 
  | { type: 'lesson'; data: Lesson }
  | { type: 'link'; data: ExternalLink }
  | { type: 'exam'; data: ExamSummary }
  | { type: 'asset_material'; data: AssetMaterial };

type TrackableStatus = 'completed' | 'in-progress' | 'not-started';
type CourseViewMode = 'lessons' | 'units';
type CourseFacet = 'subject' | 'level' | 'tags';

const browseUrl = (facet: CourseFacet, value: string) =>
  `/browse?${facet}=${encodeURIComponent(value)}`;

function getLessonStatus(lesson: Lesson): TrackableStatus {
  if (lesson.has_user_completed) return 'completed';
  if (lesson.has_user_chat || (lesson.user_chat_count || 0) > 0) return 'in-progress';
  return 'not-started';
}

function getExamStatus(exam: ExamSummary): TrackableStatus {
  if (exam.latest_attempt_state === 'MARKED' || exam.latest_attempt_state === 'SUBMITTED') {
    return 'completed';
  }
  if (exam.latest_attempt_state === 'IN_PROGRESS' || exam.has_user_attempt || (exam.user_attempt_count || 0) > 0) {
    return 'in-progress';
  }
  return 'not-started';
}

function statusLabel(status: TrackableStatus): string {
  if (status === 'completed') return 'Completed';
  if (status === 'in-progress') return 'In progress';
  return 'Not started';
}

function getExamBadgeLabel(exam: ExamSummary): string {
  if (
    exam.latest_attempt_state === 'MARKED' &&
    exam.latest_attempt_obtained_marks != null &&
    exam.latest_attempt_total_marks != null
  ) {
    return `${exam.latest_attempt_obtained_marks}/${exam.latest_attempt_total_marks}`;
  }
  if (exam.latest_attempt_state === 'MARKED') return 'Marked';
  if (exam.latest_attempt_state === 'SUBMITTED') return 'Submitted';
  return statusLabel(getExamStatus(exam));
}

function getUnitItems(unit: Unit): UnitItem[] {
  return [
    ...unit.lessons.map(l => ({ type: 'lesson' as const, data: l })),
    ...(unit.external_links || []).map(l => ({ type: 'link' as const, data: l })),
    ...(unit.exams || []).map(e => ({ type: 'exam' as const, data: e })),
    ...(unit.asset_materials || []).map(m => ({ type: 'asset_material' as const, data: m }))
  ].sort((a, b) => {
    const aOrder = a.type === 'lesson' ? (a.data.order_index ?? 0) : a.data.order_index;
    const bOrder = b.type === 'lesson' ? (b.data.order_index ?? 0) : b.data.order_index;
    if (aOrder !== bOrder) return aOrder - bOrder;
    const aName = a.type === 'lesson' ? a.data.title : a.data.name;
    const bName = b.type === 'lesson' ? b.data.title : b.data.name;
    return compareNatural(aName, bName);
  });
}

function getUnitProgress(unit: Unit) {
  const progressItems = [
    ...unit.lessons.map(lesson => getLessonStatus(lesson)),
    ...(unit.exams || []).map(exam => getExamStatus(exam))
  ];
  const completed = progressItems.filter(status => status === 'completed').length;
  const inProgress = progressItems.filter(status => status === 'in-progress').length;
  const total = progressItems.length;
  const started = completed + inProgress;
  return {
    completed,
    inProgress,
    total,
    started,
    completedPct: total > 0 ? Math.round((completed / total) * 100) : 0,
    startedPct: total > 0 ? Math.round((started / total) * 100) : 0
  };
}

function pluralize(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}

function renderUnitCounts(lessonCount: number, examCount: number): React.ReactNode {
  if (lessonCount === 0 && examCount === 0) return 'No lessons or exams';

  return (
    <>
      {lessonCount > 0 && <span>{pluralize(lessonCount, 'lesson')}</span>}
      {lessonCount > 0 && examCount > 0 && <span> · </span>}
      {examCount > 0 && <span className="course-unit-card-meta-exams">{pluralize(examCount, 'exam')}</span>}
    </>
  );
}

interface CourseDetailsProps {
  disabled?: boolean;
}

const CourseDetails: React.FC<CourseDetailsProps> = ({ disabled }) => {
  const navigate = useNavigate();
  const params = useParams<{ courseId: string; unitId?: string }>();
  const courseId = params.courseId ? parseInt(params.courseId, 10) : null;
  const routeUnitId = params.unitId ? parseInt(params.unitId, 10) : null;

  const [course, setCourse] = useState<CourseWithUnits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [canViewStudentProgress, setCanViewStudentProgress] = useState(false);
  const [isInMyCourses, setIsInMyCourses] = useState(false);
  const [myCoursesLoading, setMyCoursesLoading] = useState(false);
  const [resumeLesson, setResumeLesson] = useState<Lesson | null>(null);
  /** Most recent chat id for the lesson (API returns chats newest-first). */
  const [resumeLatestChatId, setResumeLatestChatId] = useState<string | null>(null);
  const [resumeLastActivityAt, setResumeLastActivityAt] = useState<string | null>(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeExam, setResumeExam] = useState<ExamSummary | null>(null);
  const [resumeLatestAttemptId, setResumeLatestAttemptId] = useState<string | null>(null);
  const [resumeExamLastActivityAt, setResumeExamLastActivityAt] = useState<string | null>(null);
  const [resumeExamLoading, setResumeExamLoading] = useState(false);
  const [courseViewMode, setCourseViewMode] = useState<CourseViewMode>('lessons');
  const [flashcardSummary, setFlashcardSummary] = useState<CourseFlashcardSummary | null>(null);
  const [addingFlashcards, setAddingFlashcards] = useState(false);
  const [flashcardActionError, setFlashcardActionError] = useState<string | null>(null);

  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) {
        setError('Invalid course ID');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const courseData = await api.getCourse(courseId);
        setCourse(courseData);
        setFlashcardSummary(courseData.flashcard_summary || null);
      } catch (err) {
        setError('Failed to load course');
        console.error('Error fetching course:', err);
      } finally {
        setLoading(false);
      }
    };

    const loadAuthAndMyCourses = async () => {
      if (!courseId) return;
      try {
        const me = await api.me();
        if (me?.authenticated) {
          setIsAuthenticated(true);
          setCanViewStudentProgress(
            me.user.organisation_role === 'ADMIN'
            || me.user.organisation_role === 'TEACHER'
          );
          const { course_ids } = await api.getMyCourses();
          setIsInMyCourses(course_ids.includes(courseId));
        }
      } catch {
        setIsAuthenticated(false);
        setCanViewStudentProgress(false);
      }
    };

    loadCourse();
    loadAuthAndMyCourses();
  }, [courseId]);

  useEffect(() => {
    if (!course) return;
    if (routeUnitId !== null && !Number.isNaN(routeUnitId)) {
      setCourseViewMode('units');
    } else {
      setCourseViewMode((course.units || []).length > 3 ? 'units' : 'lessons');
    }
  }, [course, routeUnitId]);

  const handleToggleMyCourse = async () => {
    if (!courseId || myCoursesLoading) return;
    setMyCoursesLoading(true);
    try {
      if (isInMyCourses) {
        await api.removeMyCourse(courseId);
        setIsInMyCourses(false);
      } else {
        await api.addMyCourse(courseId);
        setIsInMyCourses(true);
      }
    } catch (err) {
      console.error('Failed to toggle my course', err);
    } finally {
      setMyCoursesLoading(false);
    }
  };

  const handleStartNewChat = (lessonId: string) => {
    navigate(`/lesson/${lessonId}/${Math.random().toString(36).substr(2, 9)}`);
  };

  const redirectToSignupForCourse = () => {
    if (!courseId) return;
    navigate(`/register?redirect=${encodeURIComponent(`/course/${courseId}`)}`);
  };

  const handleReviseFlashcards = () => {
    if (!courseId || !flashcardSummary?.deck_count) return;
    if (!isAuthenticated) {
      redirectToSignupForCourse();
      return;
    }
    const params = new URLSearchParams({
      courseId: String(courseId),
      courseTitle: course?.title || 'Course flashcards'
    });
    navigate(`/flashcards/review?${params.toString()}`);
  };

  const handleAddAllFlashcards = async () => {
    if (!courseId || addingFlashcards) return;
    if (!isAuthenticated) {
      redirectToSignupForCourse();
      return;
    }
    setAddingFlashcards(true);
    setFlashcardActionError(null);
    try {
      const result = await api.addCourseFlashcardsToDeck(courseId);
      setFlashcardSummary({
        total_count: result.total_count,
        deck_count: result.deck_count
      });
    } catch (err: any) {
      setFlashcardActionError(err?.message || 'Failed to add flashcards to your deck');
    } finally {
      setAddingFlashcards(false);
    }
  };

  const newExamAttemptId = (examId: string) => {
    const randomId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
    return `exam-attempt-${examId}-${randomId}`;
  };

  const handleStartNewExamAttempt = (examId: string) => {
    navigate(`/exam/${encodeURIComponent(examId)}/${encodeURIComponent(newExamAttemptId(examId))}`);
  };

  const handleLessonClick = (lesson: Lesson, e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled) return;
    if (!isAuthenticated) {
      redirectToSignupForCourse();
      return;
    }
    if (
      canViewStudentProgress
      || lesson.has_user_chat
      || (lesson.user_chat_count || 0) > 0
    ) {
      openResumeModal(lesson);
    } else {
      handleStartNewChat(lesson.id);
    }
  };

  const openResumeModal = async (lesson: Lesson) => {
    setResumeLesson(lesson);
    setResumeExam(null);
    setResumeLatestChatId(null);
    setResumeLastActivityAt(null);
    setResumeLoading(true);
    try {
      const res = await api.getLessonChats(lesson.id);
      const chats = res.chats || [];
      const latest = chats[0];
      setResumeLatestChatId(latest?.id ?? null);
      setResumeLastActivityAt(latest?.last_activity_at ?? null);
    } catch (e) {
      console.error('Failed to fetch chats for lesson', e);
      setResumeLatestChatId(null);
      setResumeLastActivityAt(null);
    } finally {
      setResumeLoading(false);
    }
  };

  const closeResumeModal = () => {
    setResumeLesson(null);
    setResumeLatestChatId(null);
    setResumeLastActivityAt(null);
  };

  const handleExamClick = (exam: ExamSummary, e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled) return;
    if (!isAuthenticated) {
      redirectToSignupForCourse();
      return;
    }
    if (
      canViewStudentProgress
      || exam.has_user_attempt
      || (exam.user_attempt_count || 0) > 0
    ) {
      openExamResumeModal(exam);
    } else {
      handleStartNewExamAttempt(exam.id);
    }
  };

  const openExamResumeModal = async (exam: ExamSummary) => {
    setResumeExam(exam);
    setResumeLesson(null);
    setResumeLatestAttemptId(exam.latest_attempt_id || null);
    setResumeExamLastActivityAt(exam.latest_attempt_last_activity_at || null);
    setResumeExamLoading(true);
    try {
      const res = await api.getExamAttempts(exam.id);
      const attempts = res.attempts || [];
      const latest = attempts[0];
      setResumeLatestAttemptId(latest?.id ?? exam.latest_attempt_id ?? null);
      setResumeExamLastActivityAt(latest?.last_activity_at ?? exam.latest_attempt_last_activity_at ?? null);
    } catch (e) {
      console.error('Failed to fetch attempts for exam', e);
      setResumeLatestAttemptId(exam.latest_attempt_id || null);
      setResumeExamLastActivityAt(exam.latest_attempt_last_activity_at || null);
    } finally {
      setResumeExamLoading(false);
    }
  };

  const closeExamResumeModal = () => {
    setResumeExam(null);
    setResumeLatestAttemptId(null);
    setResumeExamLastActivityAt(null);
  };

  const handleCourseViewModeChange = (mode: CourseViewMode) => {
    setCourseViewMode(mode);
    closeResumeModal();
    closeExamResumeModal();
    if (courseId && routeUnitId !== null) {
      navigate(`/course/${courseId}`);
    }
  };

  const handleUnitSummaryClick = (unitId: number) => {
    closeResumeModal();
    closeExamResumeModal();
    if (courseId) {
      navigate(`/course/${courseId}/unit/${unitId}`);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!course) return <div className="error">Course not found</div>;

  const units = course.units || [];
  const hasContent = units.some(unit => unit.lessons.length > 0 || (unit.external_links?.length || 0) > 0 || (unit.exams?.length || 0) > 0 || (unit.asset_materials?.length || 0) > 0);
  const progressItems = units.flatMap(unit => [
    ...unit.lessons.map(lesson => getLessonStatus(lesson)),
    ...(unit.exams || []).map(exam => getExamStatus(exam))
  ]);
  const completedCount = progressItems.filter(status => status === 'completed').length;
  const inProgressCount = progressItems.filter(status => status === 'in-progress').length;
  const notStartedCount = progressItems.filter(status => status === 'not-started').length;
  const totalTrackable = progressItems.length;
  const completedPct = totalTrackable > 0 ? Math.round((completedCount / totalTrackable) * 100) : 0;
  const inProgressPct = totalTrackable > 0 ? Math.round((inProgressCount / totalTrackable) * 100) : 0;
  const contentUnits = units.filter(unit => unit.lessons.length > 0 || (unit.external_links?.length || 0) > 0 || (unit.exams?.length || 0) > 0 || (unit.asset_materials?.length || 0) > 0);
  const selectedUnitId = routeUnitId !== null && !Number.isNaN(routeUnitId) ? routeUnitId : null;
  const displayedUnits = courseViewMode === 'units' && selectedUnitId !== null
    ? contentUnits.filter(unit => unit.id === selectedUnitId)
    : contentUnits;
  const selectedUnit = selectedUnitId !== null
    ? contentUnits.find(unit => unit.id === selectedUnitId)
    : null;

  return (
    <div className="course-details">
      <div className="course-header">
        <div className="course-title-row">
          <div>
            <h2 className="course-title">{course.title}</h2>
            <div className="course-info">
              {course.subjects.map(subject => (
                <Link className="course-subject" to={browseUrl('subject', subject)} key={subject}>
                  {subject}
                </Link>
              ))}
              {course.levels.map(level => (
                <Link className="course-level" to={browseUrl('level', level)} key={level}>
                  {level}
                </Link>
              ))}
              {(course.tags || []).map(tag => (
                <Link className="course-tag" to={browseUrl('tags', tag)} key={tag}>
                  {tag}
                </Link>
              ))}
            </div>
          </div>
          {isAuthenticated && (
            <button
              className={`my-courses-btn${isInMyCourses ? ' my-courses-btn--saved' : ''}`}
              onClick={handleToggleMyCourse}
              disabled={myCoursesLoading}
              type="button"
            >
              {isInMyCourses ? '★ Remove from My Courses' : '☆ Add to My Courses'}
            </button>
          )}
        </div>
        {course.detailed_description && (
          <div className="course-detailed-description course-markdown">
            <MarkdownContent content={course.detailed_description} />
          </div>
        )}
        {(totalTrackable > 0 || (flashcardSummary && flashcardSummary.total_count > 0)) && (
          <div className={`course-summary-row${
            totalTrackable > 0 && flashcardSummary && flashcardSummary.total_count > 0
              ? ' course-summary-row--split'
              : ''
          }`}>
            {totalTrackable > 0 && (
              <div className="course-progress-card">
                <div className="course-progress-summary">
                  <div>
                    <span className="course-progress-eyebrow">Course progress</span>
                    <strong>{completedPct}% complete</strong>
                  </div>
                  <span className="course-progress-total">
                    {completedCount} of {totalTrackable} lessons/exams complete
                  </span>
                </div>
                <div className="course-progress-track" aria-hidden="true">
                  <div className="course-progress-fill course-progress-fill--completed" style={{ width: `${completedPct}%` }} />
                  <div
                    className="course-progress-fill course-progress-fill--in-progress"
                    style={{ left: `${completedPct}%`, width: `${inProgressPct}%` }}
                  />
                </div>
                <div className="course-status-legend">
                  <span><i className="course-status-dot course-status-dot--completed" />{completedCount} completed</span>
                  <span><i className="course-status-dot course-status-dot--in-progress" />{inProgressCount} in progress</span>
                  <span><i className="course-status-dot course-status-dot--not-started" />{notStartedCount} not started</span>
                </div>
              </div>
            )}
            {flashcardSummary && flashcardSummary.total_count > 0 && (
              <div className="course-flashcard-panel">
                <div className="course-flashcard-panel-copy">
                  <span className="course-progress-eyebrow">Flashcard deck</span>
                  <strong>
                    {flashcardSummary.deck_count} of {flashcardSummary.total_count} in your deck
                  </strong>
                  {flashcardSummary.deck_count === 0 && (
                    <p>Add this course's flashcards to your deck before revising them.</p>
                  )}
                  {flashcardActionError && (
                    <p className="course-flashcard-error" role="alert">{flashcardActionError}</p>
                  )}
                </div>
                <div className="course-flashcard-actions">
                  <button
                    type="button"
                    className="course-flashcard-btn course-flashcard-btn--primary"
                    onClick={handleReviseFlashcards}
                    disabled={flashcardSummary.deck_count === 0}
                    title={flashcardSummary.deck_count === 0 ? 'Add flashcards to your deck before revising' : undefined}
                  >
                    Revise flashcards
                  </button>
                  <button
                    type="button"
                    className="course-flashcard-btn"
                    onClick={() => void handleAddAllFlashcards()}
                    disabled={addingFlashcards || flashcardSummary.deck_count >= flashcardSummary.total_count}
                  >
                    {addingFlashcards
                      ? 'Adding…'
                      : flashcardSummary.deck_count >= flashcardSummary.total_count
                        ? 'All flashcards added'
                        : 'Add all to deck'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!hasContent ? (
        <div className="empty-state">
          <p>No content in this course yet.</p>
        </div>
      ) : (
        <>
          <div className="course-view-controls">
            <div className="course-view-toggle" role="group" aria-label="Course content view">
              <button
                type="button"
                className={courseViewMode === 'units' ? 'active' : ''}
                onClick={() => handleCourseViewModeChange('units')}
                aria-pressed={courseViewMode === 'units'}
              >
                Show units
              </button>
              <button
                type="button"
                className={courseViewMode === 'lessons' ? 'active' : ''}
                onClick={() => handleCourseViewModeChange('lessons')}
                aria-pressed={courseViewMode === 'lessons'}
              >
                Show all lessons
              </button>
            </div>
            {courseViewMode === 'units' && selectedUnit && (
              <button
                type="button"
                className="course-unit-back-btn"
                onClick={() => {
                  if (courseId) {
                    navigate(`/course/${courseId}`);
                  }
                }}
              >
                ← All units
              </button>
            )}
          </div>

          {courseViewMode === 'units' && selectedUnitId === null ? (
            <div className="course-units-grid">
              {contentUnits.map((unit) => {
                const unitProgress = getUnitProgress(unit);
                const lessonCount = unit.lessons.length;
                const examCount = (unit.exams || []).length;
                const unitBackgroundColor = unit.color || '#fff';

                return (
                  <button
                    key={unit.id}
                    type="button"
                    className="course-unit-card"
                    style={{ backgroundColor: unitBackgroundColor }}
                    onClick={() => handleUnitSummaryClick(unit.id)}
                  >
                    <div className="course-unit-card-main">
                      <span className="course-unit-card-title">{unit.name}</span>
                      <span className="course-unit-card-meta">
                        {renderUnitCounts(lessonCount, examCount)}
                      </span>
                    </div>
                    {unitProgress.total > 0 && (
                      <div className="course-unit-card-progress">
                        <div className="dashboard-progress-bar-track">
                          <div
                            className="dashboard-progress-bar-fill dashboard-progress-bar-started"
                            style={{ width: `${unitProgress.startedPct}%` }}
                          />
                          <div
                            className="dashboard-progress-bar-fill dashboard-progress-bar-completed"
                            style={{ width: `${unitProgress.completedPct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="course-directory">
              {displayedUnits.map((unit) => {
                const items = getUnitItems(unit);
                const unitProgress = getUnitProgress(unit);
                const unitCompleted = unitProgress.completed;
                const unitInProgress = unitProgress.inProgress;
                const unitTotal = unitProgress.total;
                const unitBackgroundColor = unit.color || '#fff';

                return (
                  <section
                    key={unit.id}
                    className="course-subject-group"
                    style={{ backgroundColor: unitBackgroundColor }}
                  >
                <div className="course-subject-heading-row">
                  <div>
                    <h2 className="course-subject-heading">{unit.name}</h2>
                  </div>
                  {unitTotal > 0 && (
                    <div className="course-unit-progress">
                      <strong>{unitCompleted}/{unitTotal}</strong>
                      <span>
                        {unitCompleted === unitTotal
                          ? 'complete'
                          : unitInProgress > 0
                            ? `${unitInProgress} active`
                            : 'not started'}
                      </span>
                    </div>
                  )}
                </div>
                <ul className="course-links-list">
                  {items.map((item) => {
                    if (item.type === 'lesson') {
                      const lesson = item.data;
                      const lessonStatus = getLessonStatus(lesson);
                      return (
                        <li key={`lesson-${lesson.id}`} className={resumeLesson?.id === lesson.id ? 'course-link-row--active' : ''}>
                          <Link
                            to={disabled ? '#' : `/lesson/${lesson.id}`}
                            className={`course-link-item course-link-item--${lessonStatus} ${disabled ? 'disabled' : ''}`}
                            onClick={(e) => handleLessonClick(lesson, e)}
                            aria-expanded={resumeLesson?.id === lesson.id}
                          >
                            <span className="course-link-status-marker" aria-hidden="true" />
                            <span className="course-link-content">
                              <span className="course-link-meta-row">
                                <span className="course-link-kind">Lesson</span>
                                <span className={`course-link-badge course-link-badge--${lessonStatus}`}>
                                  {statusLabel(lessonStatus)}
                                </span>
                              </span>
                              <span className="course-link-title">{lesson.title}</span>
                            </span>
                          </Link>
                          {resumeLesson?.id === lesson.id && (
                            <div className="course-resume-popover">
                              <div className="course-resume-popover-arrow" aria-hidden="true" />
                              <div className="course-resume-popover-header">
                                <div>
                                  <span>{resumeLatestChatId ? 'Pick up where you left off' : 'Lesson options'}</span>
                                  {resumeLastActivityAt && (
                                    <p>Last active {formatTimeAgo(resumeLastActivityAt)}</p>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="course-resume-close"
                                  onClick={closeResumeModal}
                                  aria-label="Close resume options"
                                >
                                  ×
                                </button>
                              </div>
                              {resumeLoading ? (
                                <div className="course-resume-loading">Loading latest chat…</div>
                              ) : (
                                <div className="course-resume-actions">
                                  {resumeLatestChatId ? (
                                    <>
                                      <button
                                        type="button"
                                        className="course-resume-btn course-resume-btn-primary"
                                        onClick={() => {
                                          if (!resumeLesson || !resumeLatestChatId) return;
                                          closeResumeModal();
                                          navigate(`/lesson/${resumeLesson.id}/${resumeLatestChatId}`);
                                        }}
                                      >
                                        Continue
                                      </button>
                                      <button
                                        type="button"
                                        className="course-resume-btn course-resume-btn-secondary"
                                        onClick={() => {
                                          if (!resumeLesson) return;
                                          closeResumeModal();
                                          handleStartNewChat(resumeLesson.id);
                                        }}
                                      >
                                        Start over
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="course-resume-btn course-resume-btn-primary"
                                      onClick={() => {
                                        if (!resumeLesson) return;
                                        closeResumeModal();
                                        handleStartNewChat(resumeLesson.id);
                                      }}
                                    >
                                      Start lesson
                                    </button>
                                  )}
                                </div>
                              )}
                              {canViewStudentProgress && (
                                <button
                                  type="button"
                                  className="course-progress-view-btn"
                                  onClick={() => {
                                    if (!resumeLesson) return;
                                    navigate(`/organisation/progress/lesson/${encodeURIComponent(resumeLesson.id)}`);
                                  }}
                                >
                                  View student progress
                                </button>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    } else if (item.type === 'exam') {
                      const exam = item.data;
                      const examStatus = getExamStatus(exam);
                      return (
                        <li key={`exam-${exam.id}`} className={resumeExam?.id === exam.id ? 'course-link-row--active' : ''}>
                          <Link
                            to={disabled ? '#' : `/exam/${encodeURIComponent(exam.id)}`}
                            className={`course-link-item course-link-item--${examStatus} exam-link ${disabled ? 'disabled' : ''}`}
                            onClick={(e) => handleExamClick(exam, e)}
                            aria-expanded={resumeExam?.id === exam.id}
                          >
                            <span className="course-link-status-marker" aria-hidden="true" />
                            <span className="course-link-content">
                              <span className="course-link-meta-row">
                                <span className="course-link-kind course-link-kind--exam">Exam</span>
                                <span className={`course-link-badge course-link-badge--${examStatus}`}>
                                  {getExamBadgeLabel(exam)}
                                </span>
                              </span>
                              <span className="course-link-title">{exam.name}</span>
                            </span>
                          </Link>
                          {resumeExam?.id === exam.id && (
                            <div className="course-resume-popover course-resume-popover--exam">
                              <div className="course-resume-popover-arrow" aria-hidden="true" />
                              <div className="course-resume-popover-header">
                                <div>
                                  <span>{resumeLatestAttemptId ? 'Resume your exam attempt' : 'Exam options'}</span>
                                  {resumeExamLastActivityAt && (
                                    <p>Last active {formatTimeAgo(resumeExamLastActivityAt)}</p>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="course-resume-close"
                                  onClick={closeExamResumeModal}
                                  aria-label="Close resume options"
                                >
                                  ×
                                </button>
                              </div>
                              {resumeExamLoading ? (
                                <div className="course-resume-loading">Loading latest attempt…</div>
                              ) : (
                                <div className="course-resume-actions">
                                  {resumeLatestAttemptId ? (
                                    <>
                                      <button
                                        type="button"
                                        className="course-resume-btn course-resume-btn-primary"
                                        onClick={() => {
                                          if (!resumeExam || !resumeLatestAttemptId) return;
                                          closeExamResumeModal();
                                          navigate(`/exam/${encodeURIComponent(resumeExam.id)}/${encodeURIComponent(resumeLatestAttemptId)}`);
                                        }}
                                      >
                                        Continue
                                      </button>
                                      <button
                                        type="button"
                                        className="course-resume-btn course-resume-btn-secondary"
                                        onClick={() => {
                                          if (!resumeExam) return;
                                          const examId = resumeExam.id;
                                          closeExamResumeModal();
                                          handleStartNewExamAttempt(examId);
                                        }}
                                      >
                                        Start over
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      className="course-resume-btn course-resume-btn-primary"
                                      onClick={() => {
                                        if (!resumeExam) return;
                                        const examId = resumeExam.id;
                                        closeExamResumeModal();
                                        handleStartNewExamAttempt(examId);
                                      }}
                                    >
                                      Start exam
                                    </button>
                                  )}
                                </div>
                              )}
                              {canViewStudentProgress && (
                                <button
                                  type="button"
                                  className="course-progress-view-btn"
                                  onClick={() => {
                                    if (!resumeExam) return;
                                    navigate(`/organisation/progress/exam/${encodeURIComponent(resumeExam.id)}`);
                                  }}
                                >
                                  View student progress
                                </button>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    } else if (item.type === 'link') {
                      const link = item.data;
                      return (
                        <li key={`link-${link.id}`}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="course-link-item external-link"
                          >
                            <span className="course-link-status-marker" aria-hidden="true" />
                            <span className="course-link-content">
                              <span className="course-link-meta-row">
                                <span className="course-link-kind course-link-kind--resource">External link</span>
                              </span>
                              <span className="course-link-title">{link.name}</span>
                            </span>
                            <span className="external-link-icon">Open</span>
                          </a>
                        </li>
                      );
                    } else {
                      const mat = item.data as AssetMaterial;
                      return (
                        <li key={`asset-mat-${mat.id}`}>
                          <a
                            href={api.assetDownloadUrl(mat.asset_id)}
                            className="course-link-item asset-material-link"
                          >
                            <span className="course-link-status-marker" aria-hidden="true" />
                            <span className="course-link-content">
                              <span className="course-link-meta-row">
                                <span className="course-link-kind course-link-kind--material">Material</span>
                              </span>
                              <span className="course-link-title">{mat.name}</span>
                              {mat.file_name && (
                                <span className="course-link-description">{mat.file_name}</span>
                              )}
                            </span>
                            <span className="asset-material-icon">Download</span>
                          </a>
                        </li>
                      );
                    }
                  })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default CourseDetails;
